import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Settings, Calendar, Pencil, Trash2, ExternalLink, Clock, Download, Users, FileText, Camera, Eye, Paperclip, CalendarDays, Timer, Wallet, Upload, BookOpen, MessageSquare, UserCog, ClipboardList, HardHat, Shield, Truck, Calculator, ListChecks, Link, Save, Archive, ArchiveRestore } from 'lucide-react';
import { Pagination, usePagination } from '../components/Pagination';
import { Textarea } from '../components/ui/textarea';

const API_URL = '';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

const POLICY_TYPES = [
  { value: 'handbook', label: 'Employee Handbook' },
  { value: 'labor_law', label: 'Labor Law Entitlements' },
  { value: 'dress_code', label: 'Dress Code Policy' },
  { value: 'leave_policy', label: 'Leave Policy' },
  { value: 'safety', label: 'Safety Guidelines' },
  { value: 'other', label: 'Other Policy' }
];

// Role options with access levels
const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin', access: 'full', color: 'bg-violet-500/20 text-violet-500' },
  { value: 'principal', label: 'Principal', access: 'full', color: 'bg-blue-500/20 text-blue-500' },
  { value: 'hr', label: 'HR', access: 'full', color: 'bg-emerald-500/20 text-emerald-500' },
  { value: 'project_manager', label: 'Project Manager', access: 'projects', color: 'bg-amber-500/20 text-amber-500' },
  { value: 'design_lead', label: 'Design Lead', access: 'projects', color: 'bg-cyan-500/20 text-cyan-500' },
  { value: 'design', label: 'Design', access: 'employee', color: 'bg-muted text-muted-foreground' },
  { value: 'construction', label: 'Construction', access: 'employee', color: 'bg-muted text-muted-foreground' },
];

// Department options
const DEPARTMENT_OPTIONS = [
  { value: 'Design', label: 'Design' },
  { value: 'Construction', label: 'Construction' },
  { value: 'Admin', label: 'Admin' },
  { value: 'HR', label: 'HR' },
  { value: 'Management', label: 'Management' },
];

const FULL_ACCESS_ROLES = ['admin', 'principal', 'hr'];
const PROJECT_ONLY_ROLES = ['project_manager', 'design_lead'];

export default function AdminDepartment({ user, onLogout, theme, onThemeToggle }) {
  const location = useLocation();
  const [designSchedules, setDesignSchedules] = useState([]);
  const [constructionSchedules, setConstructionSchedules] = useState([]);
  const [projects, setProjects] = useState([]);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [dtrEntries, setDtrEntries] = useState([]);
  const [dprs, setDprs] = useState([]);
  const [reimbursements, setReimbursements] = useState([]);
  const [rfis, setRfis] = useState([]);
  const [leaveApplications, setLeaveApplications] = useState([]);
  const [otApplications, setOtApplications] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [itrDocuments, setItrDocuments] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [departmentPolicyLinks, setDepartmentPolicyLinks] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedLeaveUserId, setSelectedLeaveUserId] = useState('');
  const [selectedOtUserId, setSelectedOtUserId] = useState('');
  const [showAllOt, setShowAllOt] = useState(false);
  const [selectedPayVaultUserId, setSelectedPayVaultUserId] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showDprDialog, setShowDprDialog] = useState(false);
  const [showPayslipDialog, setShowPayslipDialog] = useState(false);
  const [showItrDialog, setShowItrDialog] = useState(false);
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [showLeaveCreditsDialog, setShowLeaveCreditsDialog] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [selectedEmployeeForCredits, setSelectedEmployeeForCredits] = useState(null);
  const [leaveCreditsForm, setLeaveCreditsForm] = useState({ vacation_leave: 15, sick_leave: 10, emergency_leave: 5, vacation_used: 0, sick_used: 0, emergency_used: 0 });
  const [selectedDpr, setSelectedDpr] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [currentDept, setCurrentDept] = useState('design');
  const [scheduleForm, setScheduleForm] = useState({ project_name: '', instagantt_link: '', department: 'design' });

  // ===== Pagination for long lists (20 per page) =====
  const dprPage = usePagination(dprs, 20);
  const rfiPage = usePagination(rfis, 20);
  const reimbPage = usePagination(reimbursements, 20);
  const auditPage = usePagination(auditLogs, 20);
  const employeePage = usePagination(users, 20);
  
  // Tab state - check if navigated from dashboard with specific tab
  const [activeCategory, setActiveCategory] = useState(() => {
    const tab = location.state?.activeTab;
    return (tab === 'leave' || tab === 'ot') ? 'hr' : 'projects';
  });
  const [activeHrTab, setActiveHrTab] = useState(() => {
    const tab = location.state?.activeTab;
    return tab === 'leave' ? 'leave' : tab === 'ot' ? 'ot' : 'dtr';
  });
  const [projectForm, setProjectForm] = useState({ project_name: '', project_code: '', description: '', client_name: '', project_address: '' });
  const [payslipForm, setPayslipForm] = useState({ user_id: '', month: new Date().getMonth() + 1, year: new Date().getFullYear(), file: null });
  const [itrForm, setItrForm] = useState({ user_id: '', year: new Date().getFullYear(), file: null });
  const [policyForm, setPolicyForm] = useState({ policy_type: 'handbook', title: '', description: '', version: '1.0', file: null });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    axios.get(`${API_URL}/api/project-schedules?department=design`)
      .then(res => setDesignSchedules(res.data))
      .catch(err => console.error(err));
    axios.get(`${API_URL}/api/project-schedules?department=construction`)
      .then(res => setConstructionSchedules(res.data))
      .catch(err => console.error(err));
    axios.get(`${API_URL}/api/projects`)
      .then(res => setProjects(res.data))
      .catch(err => console.error(err));
    axios.get(`${API_URL}/api/projects?archived_only=true`)
      .then(res => setArchivedProjects(res.data))
      .catch(err => console.error(err));
    axios.get(`${API_URL}/api/users`)
      .then(res => setUsers(res.data))
      .catch(err => console.error(err));
    axios.get(`${API_URL}/api/dtr`)
      .then(res => setDtrEntries(res.data))
      .catch(err => console.error(err));
    axios.get(`${API_URL}/api/dpr`)
      .then(res => setDprs(res.data))
      .catch(err => console.error(err));
    axios.get(`${API_URL}/api/reimbursements`)
      .then(res => setReimbursements(res.data))
      .catch(err => console.error(err));
    axios.get(`${API_URL}/api/rfi`)
      .then(res => setRfis(res.data))
      .catch(err => console.error(err));
    axios.get(`${API_URL}/api/leave`)
      .then(res => setLeaveApplications(res.data))
      .catch(err => console.error(err));
    axios.get(`${API_URL}/api/ot`)
      .then(res => setOtApplications(res.data))
      .catch(err => console.error(err));
    axios.get(`${API_URL}/api/payslips/all`)
      .then(res => setPayslips(res.data))
      .catch(err => console.error(err));
    axios.get(`${API_URL}/api/itr/all`)
      .then(res => setItrDocuments(res.data))
      .catch(err => console.error(err));
    axios.get(`${API_URL}/api/policies`)
      .then(res => setPolicies(res.data))
      .catch(err => console.error(err));
    // Load department policy links (for new policy link editor)
    axios.get(`${API_URL}/api/department-policies/admin/all`)
      .then(res => setDepartmentPolicyLinks(res.data))
      .catch(err => console.error(err));
    // Load audit logs only for principal
    if (user.role === 'principal') {
      axios.get(`${API_URL}/api/audit-logs`)
        .then(res => setAuditLogs(res.data))
        .catch(err => console.error(err));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSchedule) {
        await axios.put(`${API_URL}/api/project-schedules/${editingSchedule.id}`, scheduleForm, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        });
        toast.success('Project schedule updated!');
      } else {
        await axios.post(`${API_URL}/api/project-schedules`, scheduleForm, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        });
        toast.success('Project schedule created!');
      }
      setScheduleForm({ project_name: '', instagantt_link: '', department: 'design' });
      setEditingSchedule(null);
      setShowDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save project schedule');
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setScheduleForm({
      project_name: schedule.project_name,
      instagantt_link: schedule.instagantt_link,
      department: schedule.department
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project schedule?')) return;
    try {
      await axios.delete(`${API_URL}/api/project-schedules/${id}`);
      toast.success('Project schedule deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete project schedule');
    }
  };

  const handleSubmitProject = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await axios.put(`${API_URL}/api/projects/${editingProject.id}`, projectForm, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        });
        toast.success('Project updated!');
      } else {
        await axios.post(`${API_URL}/api/projects`, projectForm, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        });
        toast.success('Project created!');
      }
      setProjectForm({ project_name: '', project_code: '', description: '', client_name: '', project_address: '' });
      setEditingProject(null);
      setShowProjectDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save project');
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setProjectForm({
      project_name: project.project_name,
      project_code: project.project_code,
      description: project.description,
      client_name: project.client_name || '',
      project_address: project.project_address || ''
    });
    setShowProjectDialog(true);
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API_URL}/api/projects/${id}`);
      toast.success('Project deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleArchiveProject = async (id) => {
    if (!window.confirm('Archive this project? It will be hidden from all departments, project dropdowns, POs, and reports until you restore it. No data is deleted.')) return;
    try {
      await axios.patch(`${API_URL}/api/projects/${id}/archive`);
      toast.success('Project archived');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive project');
    }
  };

  const handleRestoreProject = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/projects/${id}/unarchive`);
      toast.success('Project restored — it is now visible everywhere again');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore project');
    }
  };

  const openNewDialog = (dept) => {
    setCurrentDept(dept);
    setEditingSchedule(null);
    setScheduleForm({ project_name: '', instagantt_link: '', department: dept });
    setShowDialog(true);
  };

  const handleExportDtr = async () => {
    try {
      const url = selectedUserId 
        ? `${API_URL}/api/dtr/export/excel?user_id=${selectedUserId}` 
        : `${API_URL}/api/dtr/export/excel`;
      
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `DTR_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('DTR exported successfully!');
    } catch (error) {
      toast.error('Failed to export DTR');
    }
  };

  const handleExportDpr = async (dprId) => {
    try {
      const response = await axios.get(`${API_URL}/api/dpr/${dprId}/export`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `DPR_${dprId}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('DPR exported successfully!');
    } catch (error) {
      toast.error('Failed to export DPR');
    }
  };

  const handleExportAllDpr = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dpr/export/excel`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `DPR_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('All DPRs exported to Excel!');
    } catch (error) {
      toast.error('Failed to export DPRs');
    }
  };

  const handleViewDpr = (dpr) => {
    setSelectedDpr(dpr);
    setShowDprDialog(true);
  };

  const handleApproveReimbursement = async (reimbId) => {
    try {
      await axios.put(`${API_URL}/api/reimbursements/${reimbId}`,
        new URLSearchParams({ status: 'approved' }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
      );
      toast.success('Reimbursement approved!');
      loadData();
    } catch (error) {
      toast.error('Failed to approve reimbursement');
    }
  };

  const handleDeclineReimbursement = async (reimbId) => {
    if (!window.confirm('Are you sure you want to decline this reimbursement?')) return;
    try {
      await axios.put(`${API_URL}/api/reimbursements/${reimbId}`,
        new URLSearchParams({ status: 'rejected' }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
      );
      toast.success('Reimbursement declined');
      loadData();
    } catch (error) {
      toast.error('Failed to decline reimbursement');
    }
  };

  const handleSetReimbStatus = async (reimbId, newStatus, label) => {
    try {
      await axios.put(`${API_URL}/api/reimbursements/${reimbId}`,
        new URLSearchParams({ status: newStatus }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
      );
      toast.success(`Reimbursement marked ${label}`);
      loadData();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleExportReimbursement = async (reimbId) => {
    try {
      const response = await axios.get(`${API_URL}/api/reimbursement/${reimbId}/export`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `Reimbursement_${reimbId.slice(0,8)}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Reimbursement exported!');
    } catch (error) {
      toast.error('Failed to export reimbursement');
    }
  };

  const handleDeleteReimbursement = async (reimbId) => {
    if (!window.confirm('Are you sure you want to delete this reimbursement submission? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/reimbursements/${reimbId}`);
      toast.success('Reimbursement deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete reimbursement');
    }
  };

  // Department Policy Link Handlers
  const handleUpdatePolicyLink = async (departmentId, policyTypeId, googleDriveLink) => {
    try {
      const formData = new FormData();
      formData.append('department_id', departmentId);
      formData.append('policy_type_id', policyTypeId);
      formData.append('google_drive_link', googleDriveLink);
      
      await axios.post(`${API_URL}/api/department-policies/admin/update`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Policy link updated!');
      loadData();
    } catch (error) {
      toast.error('Failed to update policy link');
    }
  };

  const handleDeletePolicyLink = async (departmentId, policyTypeId) => {
    if (!window.confirm('Are you sure you want to remove this policy link?')) return;
    try {
      await axios.delete(`${API_URL}/api/department-policies/admin/${departmentId}/${policyTypeId}`);
      toast.success('Policy link removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove policy link');
    }
  };

  const handleDeleteRfi = async (rfiId) => {
    if (!window.confirm('Are you sure you want to delete this RFI submission? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/rfi/${rfiId}`);
      toast.success('RFI deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete RFI');
    }
  };

  const handleDeleteDtr = async (dtrId) => {
    if (!window.confirm('Are you sure you want to delete this DTR entry? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/dtr/${dtrId}`);
      toast.success('DTR entry deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete DTR entry');
    }
  };

  const handleClearDtrLogs = async () => {
    const filterText = selectedUserId 
      ? `all DTR entries for ${users.find(u => u.id === selectedUserId)?.full_name || 'selected user'}`
      : 'ALL DTR entries for ALL employees';
    
    if (!window.confirm(`Are you sure you want to clear ${filterText}? This action cannot be undone!`)) return;
    
    try {
      const params = selectedUserId ? `?user_id=${selectedUserId}` : '';
      const response = await axios.delete(`${API_URL}/api/dtr/clear/all${params}`);
      toast.success(`Cleared ${response.data.deleted_count} DTR entries`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear DTR logs');
    }
  };

  const handleDeleteLeave = async (leaveId) => {
    if (!window.confirm('Are you sure you want to delete this leave application? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/leave/${leaveId}`);
      toast.success('Leave application deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete leave application');
    }
  };

  const handleDeleteOt = async (otId) => {
    if (!window.confirm('Are you sure you want to delete this OT application? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/ot/${otId}`);
      toast.success('OT application deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete OT application');
    }
  };

  const handleDeleteDpr = async (dprId) => {
    if (!window.confirm('Are you sure you want to delete this Daily Progress Report? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/dpr/${dprId}`);
      toast.success('Daily Progress Report deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete Daily Progress Report');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This will also delete all their applications, submissions, and records. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/users/${userId}`);
      toast.success(`${userName} deleted successfully`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openLeaveCreditsDialog = async (employee) => {
    setSelectedEmployeeForCredits(employee);
    try {
      const res = await axios.get(`${API_URL}/api/leave/credits/${employee.id}`);
      setLeaveCreditsForm({
        vacation_leave: res.data.vacation_leave || 15,
        sick_leave: res.data.sick_leave || 10,
        emergency_leave: res.data.emergency_leave || 5,
        vacation_used: res.data.vacation_used || 0,
        sick_used: res.data.sick_used || 0,
        emergency_used: res.data.emergency_used || 0
      });
      setShowLeaveCreditsDialog(true);
    } catch (error) {
      toast.error('Failed to load leave credits');
    }
  };

  const handleUpdateLeaveCredits = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('vacation_leave', leaveCreditsForm.vacation_leave);
      formData.append('sick_leave', leaveCreditsForm.sick_leave);
      formData.append('emergency_leave', leaveCreditsForm.emergency_leave);
      formData.append('vacation_used', leaveCreditsForm.vacation_used);
      formData.append('sick_used', leaveCreditsForm.sick_used);
      formData.append('emergency_used', leaveCreditsForm.emergency_used);
      
      await axios.put(`${API_URL}/api/leave/credits/${selectedEmployeeForCredits.id}`, formData);
      toast.success(`Leave credits updated for ${selectedEmployeeForCredits.full_name}`);
      setShowLeaveCreditsDialog(false);
      setSelectedEmployeeForCredits(null);
    } catch (error) {
      toast.error('Failed to update leave credits');
    }
  };

  const filteredDtrEntries = selectedUserId 
    ? dtrEntries.filter(e => e.user_id === selectedUserId)
    : dtrEntries;

  const filteredLeaveApplications = selectedLeaveUserId
    ? leaveApplications.filter(e => e.user_id === selectedLeaveUserId)
    : leaveApplications;

  const filteredOtApplications = selectedOtUserId
    ? otApplications.filter(e => e.user_id === selectedOtUserId)
    : otApplications;

  const handleApproveLeave = async (leaveId) => {
    try {
      await axios.put(`${API_URL}/api/leave/${leaveId}`, 
        new URLSearchParams({ status: 'approved' }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
      );
      toast.success('Leave application approved!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve leave');
    }
  };

  const handleDeclineLeave = async (leaveId) => {
    if (!window.confirm('Are you sure you want to decline this leave application?')) return;
    try {
      await axios.put(`${API_URL}/api/leave/${leaveId}`,
        new URLSearchParams({ status: 'rejected' }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
      );
      toast.success('Leave application declined');
      loadData();
    } catch (error) {
      toast.error('Failed to decline leave');
    }
  };

  const handleApproveOt = async (otId) => {
    try {
      await axios.put(`${API_URL}/api/ot/${otId}`,
        new URLSearchParams({ status: 'approved' }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
      );
      toast.success('OT application approved!');
      loadData();
    } catch (error) {
      toast.error('Failed to approve OT');
    }
  };

  const handleDeclineOt = async (otId) => {
    if (!window.confirm('Are you sure you want to decline this OT application?')) return;
    try {
      await axios.put(`${API_URL}/api/ot/${otId}`,
        new URLSearchParams({ status: 'rejected' }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
      );
      toast.success('OT application declined');
      loadData();
    } catch (error) {
      toast.error('Failed to decline OT');
    }
  };

  const handleExportLeaveSummary = async () => {
    try {
      const url = selectedLeaveUserId
        ? `${API_URL}/api/leave/export/excel?user_id=${selectedLeaveUserId}`
        : `${API_URL}/api/leave/export/excel`;

      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `Leave_Summary_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Leave summary exported!');
    } catch (error) {
      toast.error('Failed to export leave summary');
    }
  };

  const handleExportOtSummary = async () => {
    try {
      const url = selectedOtUserId
        ? `${API_URL}/api/ot/summary/export?user_id=${selectedOtUserId}`
        : `${API_URL}/api/ot/summary/export`;

      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `OT_Summary_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('OT summary exported!');
    } catch (error) {
      toast.error('Failed to export OT summary');
    }
  };

  const getLeaveTypeName = (type) => {
    const names = { VL: 'Vacation Leave', SL: 'Sick Leave', Emergency: 'Emergency Leave' };
    return names[type] || type;
  };

  // Pay Vault handlers
  const filteredPayslips = selectedPayVaultUserId
    ? payslips.filter(p => p.user_id === selectedPayVaultUserId)
    : payslips;

  const filteredItrDocuments = selectedPayVaultUserId
    ? itrDocuments.filter(d => d.user_id === selectedPayVaultUserId)
    : itrDocuments;

  const handlePayslipUpload = async (e) => {
    e.preventDefault();
    if (!payslipForm.file || !payslipForm.user_id) {
      toast.error('Please select an employee and upload a PDF file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', payslipForm.file);
      formData.append('user_id', payslipForm.user_id);
      formData.append('month', payslipForm.month);
      formData.append('year', payslipForm.year);

      await axios.post(`${API_URL}/api/payslips`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Payslip uploaded successfully!');
      setShowPayslipDialog(false);
      setPayslipForm({ user_id: '', month: new Date().getMonth() + 1, year: new Date().getFullYear(), file: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload payslip');
    } finally {
      setUploading(false);
    }
  };

  const handleItrUpload = async (e) => {
    e.preventDefault();
    if (!itrForm.file || !itrForm.user_id) {
      toast.error('Please select an employee and upload a PDF file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', itrForm.file);
      formData.append('user_id', itrForm.user_id);
      formData.append('year', itrForm.year);

      await axios.post(`${API_URL}/api/itr`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('ITR document uploaded successfully!');
      setShowItrDialog(false);
      setItrForm({ user_id: '', year: new Date().getFullYear(), file: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload ITR document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePayslip = async (payslipId) => {
    if (!window.confirm('Are you sure you want to delete this payslip?')) return;
    try {
      await axios.delete(`${API_URL}/api/payslips/${payslipId}`);
      toast.success('Payslip deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete payslip');
    }
  };

  const handleDeleteItr = async (itrId) => {
    if (!window.confirm('Are you sure you want to delete this ITR document?')) return;
    try {
      await axios.delete(`${API_URL}/api/itr/${itrId}`);
      toast.success('ITR document deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete ITR document');
    }
  };

  // Handle role change for employees
  const handleRoleChange = async (userId, newRole, userName) => {
    try {
      const formData = new FormData();
      formData.append('role', newRole);
      
      await axios.put(`${API_URL}/api/users/${userId}/role`, formData);
      toast.success(`Updated ${userName}'s role to ${ROLE_OPTIONS.find(r => r.value === newRole)?.label || newRole}`);
      loadData(); // Refresh user list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  // Handle department change for employees
  const handleDepartmentChange = async (userId, newDepartment, userName) => {
    try {
      const formData = new FormData();
      formData.append('department', newDepartment);
      
      await axios.put(`${API_URL}/api/users/${userId}/department`, formData);
      toast.success(`Updated ${userName}'s department to ${newDepartment}`);
      loadData(); // Refresh user list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update department');
    }
  };

  // Get role color class
  const getRoleColor = (role) => {
    const roleOption = ROLE_OPTIONS.find(r => r.value === role);
    return roleOption?.color || 'bg-muted text-muted-foreground';
  };

  // Group payslips by year for display
  const groupedPayslips = filteredPayslips.reduce((acc, payslip) => {
    const year = payslip.year;
    if (!acc[year]) acc[year] = [];
    acc[year].push(payslip);
    return acc;
  }, {});

  const sortedYears = Object.keys(groupedPayslips).sort((a, b) => b - a);

  // Policy handlers
  const handlePolicyUpload = async (e) => {
    e.preventDefault();
    if (!policyForm.file || !policyForm.title) {
      toast.error('Please fill in all required fields and upload a PDF file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', policyForm.file);
      formData.append('policy_type', policyForm.policy_type);
      formData.append('title', policyForm.title);
      formData.append('description', policyForm.description);
      formData.append('version', policyForm.version);

      const response = await axios.post(`${API_URL}/api/policies`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(response.data.message);
      setShowPolicyDialog(false);
      setPolicyForm({ policy_type: 'handbook', title: '', description: '', version: '1.0', file: null });
      setEditingPolicy(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload policy');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this policy document?')) return;
    try {
      await axios.delete(`${API_URL}/api/policies/${policyId}`);
      toast.success('Policy deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const openEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setPolicyForm({
      policy_type: policy.policy_type,
      title: policy.title,
      description: policy.description || '',
      version: policy.version || '1.0',
      file: null
    });
    setShowPolicyDialog(true);
  };

  const getPolicyTypeLabel = (type) => {
    const found = POLICY_TYPES.find(p => p.value === type);
    return found ? found.label : type;
  };

  return (
    <Layout user={user} onLogout={onLogout} theme={theme} onThemeToggle={onThemeToggle}>
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 lg:space-y-8" data-testid="admin-department-page">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/20 rounded-md flex items-center justify-center flex-shrink-0">
            <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground truncate">Admin Department</h1>
            <p className="text-sm sm:text-base text-muted-foreground">Manage project schedules and system settings</p>
          </div>
        </div>

        {/* Role-based access message for limited access users */}
        {PROJECT_ONLY_ROLES.includes(user.role) && (
          <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-md">
            <p className="text-sm text-amber-500">
              <strong>Limited Access:</strong> As a {ROLE_OPTIONS.find(r => r.value === user.role)?.label}, you have access to Projects management only.
            </p>
          </div>
        )}

        <Tabs scope="admin" value={activeCategory} onValueChange={setActiveCategory} className="space-y-4 sm:space-y-6">
          <TabsList className="bg-muted/50 w-full sm:w-auto flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="projects" data-testid="tab-category-projects" className="gap-2 text-xs sm:text-sm">
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">Projects</span>
              <span className="sm:hidden">Proj</span>
            </TabsTrigger>
            {/* Only show HR tab for full access roles */}
            {FULL_ACCESS_ROLES.includes(user.role) && (
              <TabsTrigger value="hr" data-testid="tab-category-hr" className="gap-2 text-xs sm:text-sm">
                <UserCog className="w-4 h-4" />
                <span className="hidden sm:inline">HR</span>
                <span className="sm:hidden">HR</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* ========== PROJECTS CATEGORY ========== */}
          <TabsContent value="projects" className="space-y-4 sm:space-y-6">
            <Tabs scope="admin.projects" defaultValue="dpr" className="space-y-4">
              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                <TabsList className="bg-muted/30 flex-wrap h-auto gap-1 p-1 justify-start w-full sm:w-auto">
                  <TabsTrigger value="dpr" data-testid="tab-dpr-reports" className="text-xs sm:text-sm whitespace-nowrap">Daily Progress</TabsTrigger>
                  <TabsTrigger value="rfi" data-testid="tab-rfi-submissions" className="text-xs sm:text-sm whitespace-nowrap">RFI Submissions</TabsTrigger>
                  <TabsTrigger value="directory" data-testid="tab-project-directory" className="text-xs sm:text-sm whitespace-nowrap">Project Directory</TabsTrigger>
                </TabsList>
              </div>

          <TabsContent value="dpr" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="dpr-reports-card">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-accent" />
                    <CardTitle>Daily Progress Reports</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {dprs.length} report(s) | Auto-deleted after 2 months
                    </span>
                    <Button 
                      onClick={handleExportAllDpr} 
                      variant="outline"
                      size="sm"
                      data-testid="btn-export-all-dpr"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export All
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {dprs.length > 0 ? (
                  <>
                  <div className="space-y-4">
                    {dprPage.items.map((dpr) => (
                      <div 
                        key={dpr.id} 
                        className="p-4 bg-muted/30 rounded-md border border-border/40"
                        data-testid={`dpr-row-${dpr.id}`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                          <div className="min-w-0">
                            <h4 className="font-semibold text-foreground text-base sm:text-lg break-all">{dpr.project}</h4>
                            <p className="text-xs text-muted-foreground">
                              Submitted by: {dpr.submitted_by_name}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              {new Date(dpr.created_at).toLocaleDateString()}
                            </span>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleViewDpr(dpr)}
                              data-testid={`btn-view-dpr-${dpr.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            <Button 
                              size="sm" 
                              onClick={() => handleExportDpr(dpr.id)}
                              data-testid={`btn-export-dpr-${dpr.id}`}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Export PDF
                            </Button>
                            <button
                              onClick={() => handleDeleteDpr(dpr.id)}
                              className="p-2 hover:bg-muted rounded"
                              data-testid={`btn-delete-dpr-${dpr.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div>
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Accomplished Works</p>
                            <p className="text-sm text-foreground line-clamp-2">{dpr.accomplished_works}</p>
                          </div>
                          
                          {dpr.site_photos && dpr.site_photos.length > 0 && (
                            <div className="flex items-center gap-2">
                              <Camera className="w-4 h-4 text-accent" />
                              <span className="text-sm text-accent">{dpr.site_photos.length} photo(s) attached</span>
                            </div>
                          )}
                          
                          {dpr.challenges && (
                            <div>
                              <p className="text-xs font-medium text-orange-500 uppercase tracking-wide">Challenges / Urgent Items</p>
                              <p className="text-sm text-orange-400">{dpr.challenges}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Pagination page={dprPage.page} totalPages={dprPage.totalPages} setPage={dprPage.setPage} total={dprPage.total} pageSize={dprPage.pageSize} label="reports" testIdPrefix="dpr-pg" />
                  </>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-8">No daily progress reports yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="rfi" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="rfi-submissions-card">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-cyan-500" />
                    <CardTitle>RFI Submissions</CardTitle>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground mt-2">
                  {rfis.length} submission(s) | Auto-deleted after 2 months
                </span>
              </CardHeader>
              <CardContent>
                {rfis.length > 0 ? (
                  <>
                  <div className="space-y-4">
                    {rfiPage.items.map((rfi) => (
                      <div 
                        key={rfi.id} 
                        className={`p-4 rounded-md border ${
                          rfi.status === 'resolved' ? 'bg-green-500/10 border-green-500/30' :
                          rfi.status === 'responded' ? 'bg-blue-500/10 border-blue-500/30' :
                          'bg-muted/30 border-border/40'
                        }`}
                        data-testid={`rfi-row-${rfi.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-foreground">{rfi.title}</h4>
                              <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                                rfi.status === 'resolved' ? 'bg-green-500/20 text-green-500' :
                                rfi.status === 'responded' ? 'bg-blue-500/20 text-blue-500' :
                                'bg-yellow-500/20 text-yellow-500'
                              }`}>
                                {rfi.status || 'pending'}
                              </span>
                              {rfi.rfi_type && (
                                <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded">
                                  {rfi.rfi_type}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2">{rfi.description}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              Project: {rfi.project || 'N/A'} | By: {rfi.user_name || 'Unknown'} | {new Date(rfi.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 pt-2 border-t border-border/40">
                          {rfi.attachment_url && (
                            <a 
                              href={`${API_URL}${rfi.attachment_url}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-cyan-500 hover:underline"
                            >
                              <Paperclip className="w-3 h-3" />
                              View Attachment
                            </a>
                          )}
                          <button
                            onClick={() => handleDeleteRfi(rfi.id)}
                            className="p-2 hover:bg-muted rounded ml-auto"
                            data-testid={`btn-delete-rfi-${rfi.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Pagination page={rfiPage.page} totalPages={rfiPage.totalPages} setPage={rfiPage.setPage} total={rfiPage.total} pageSize={rfiPage.pageSize} label="RFIs" testIdPrefix="rfi-pg" />
                  </>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-8">No RFI submissions yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="directory" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="project-directory-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-primary" />
                    <CardTitle>Project Directory</CardTitle>
                    {showArchived && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium">Archived Folder</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={showArchived ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setShowArchived(v => !v)}
                      data-testid="btn-toggle-archived"
                    >
                      <Archive className="w-4 h-4 mr-1.5" />
                      {showArchived ? 'View Active' : `Archived (${archivedProjects.length})`}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        if (!window.confirm('This will sync project names/codes across all Gantt charts, POs, DPRs, RFIs, and Schedules to match the directory exactly. Continue?')) return;
                        try {
                          const res = await axios.post(`${API_URL}/api/projects/sync-all-references`);
                          const u = res.data?.updated || {};
                          const total = Object.values(u).reduce((s, n) => s + (n || 0), 0);
                          toast.success(total === 0 ? 'All references already in sync' : `Synced ${total} record(s) — POs:${u.purchase_orders||0} Gantt:${u.gantt_charts||0} DPRs:${u.daily_progress_reports||0} RFIs:${u.rfis||0} Schedules:${u.project_schedules||0}`);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Sync failed');
                        }
                      }}
                      data-testid="btn-sync-project-references"
                    >
                      Sync References
                    </Button>
                    <Button
                      onClick={() => {
                        setEditingProject(null);
                        setProjectForm({ project_name: '', project_code: '', description: '', client_name: '', project_address: '' });
                        setShowProjectDialog(true);
                      }}
                      data-testid="btn-add-project"
                    >
                      Add Project
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(showArchived ? archivedProjects : projects).length > 0 ? (
                    (showArchived ? archivedProjects : projects).map((project) => (
                      <div
                        key={project.id}
                        className="p-4 bg-muted/30 rounded-md hover-lift"
                        data-testid={`project-${project.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-foreground text-lg">{project.project_name}</h3>
                              <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded font-mono">
                                {project.project_code}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">{project.description}</p>
                            {(project.client_name || project.project_address) && (
                              <div className="mt-2 space-y-0.5 text-xs">
                                {project.client_name && (
                                  <p className="text-foreground/80"><span className="text-muted-foreground">Client:</span> <span className="font-medium">{project.client_name}</span></p>
                                )}
                                {project.project_address && (
                                  <p className="text-foreground/80"><span className="text-muted-foreground">Address:</span> {project.project_address}</p>
                                )}
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">
                              Added {new Date(project.created_at).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                          <div className="flex gap-2 ml-4">
                            {showArchived ? (
                              (user.role === 'admin' || user.role === 'principal') && (
                                <button
                                  onClick={() => handleRestoreProject(project.id)}
                                  className="px-2 py-1 hover:bg-emerald-50 rounded flex items-center gap-1 text-xs font-medium text-emerald-600 border border-emerald-200"
                                  data-testid={`btn-restore-project-${project.id}`}
                                >
                                  <ArchiveRestore className="w-4 h-4" /> Restore
                                </button>
                              )
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEditProject(project)}
                                  className="p-2 hover:bg-muted rounded"
                                  data-testid={`btn-edit-project-${project.id}`}
                                >
                                  <Pencil className="w-4 h-4 text-primary" />
                                </button>
                                {(user.role === 'admin' || user.role === 'principal') && (
                                  <button
                                    onClick={() => handleArchiveProject(project.id)}
                                    className="p-2 hover:bg-muted rounded"
                                    title="Archive project"
                                    data-testid={`btn-archive-project-${project.id}`}
                                  >
                                    <Archive className="w-4 h-4 text-amber-600" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteProject(project.id)}
                                  className="p-2 hover:bg-muted rounded"
                                  data-testid={`btn-delete-project-${project.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-sm text-center py-8">{showArchived ? 'No archived projects' : 'No projects yet'}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>


            </Tabs>
          </TabsContent>

          {/* ========== HR / ACCOUNTING CATEGORY ========== */}
          {FULL_ACCESS_ROLES.includes(user.role) && (
          <TabsContent value="hr" className="space-y-4 sm:space-y-6">
            <Tabs scope="admin.hr" value={activeHrTab} onValueChange={setActiveHrTab} className="space-y-4">
              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                <TabsList className="bg-muted/30 flex-wrap h-auto gap-1 p-1 justify-start w-full sm:w-auto">
                  <TabsTrigger value="dtr" data-testid="tab-dtr-submissions" className="text-xs sm:text-sm whitespace-nowrap">DTR</TabsTrigger>
                  <TabsTrigger value="ot" data-testid="tab-ot-applications" className="text-xs sm:text-sm whitespace-nowrap">OT</TabsTrigger>
                  <TabsTrigger value="leave" data-testid="tab-leave-applications" className="text-xs sm:text-sm whitespace-nowrap">Leave</TabsTrigger>
                  <TabsTrigger value="reimbursement" data-testid="tab-reimbursement" className="text-xs sm:text-sm whitespace-nowrap">Reimb.</TabsTrigger>
                  <TabsTrigger value="payvault" data-testid="tab-pay-vault" className="text-xs sm:text-sm whitespace-nowrap">Pay Vault</TabsTrigger>
                  <TabsTrigger value="employees" data-testid="tab-employees" className="text-xs sm:text-sm whitespace-nowrap">Employees</TabsTrigger>
                  <TabsTrigger value="policies" data-testid="tab-policies" className="text-xs sm:text-sm whitespace-nowrap">Policies</TabsTrigger>
                  {user.role === 'principal' && (
                    <TabsTrigger value="audit" data-testid="tab-audit-logs" className="text-xs sm:text-sm whitespace-nowrap">Audit</TabsTrigger>
                  )}
                </TabsList>
              </div>

          <TabsContent value="dtr" className="space-y-4 sm:space-y-6">
            <Card className="glass-effect border-border/40" data-testid="dtr-submissions-card">
              <CardHeader className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-primary" />
                    <CardTitle className="text-lg sm:text-xl">DTR Submissions</CardTitle>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-muted-foreground hidden sm:block" />
                      <select
                        data-testid="select-employee-filter"
                        className="flex h-9 sm:h-10 w-full sm:w-[200px] rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value)}
                      >
                        <option value="">All Employees</option>
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.full_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <Button 
                      onClick={handleExportDtr} 
                      variant="outline"
                      size="sm"
                      className="h-9 sm:h-10"
                      data-testid="btn-export-dtr"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export Excel
                    </Button>
                    <Button 
                      onClick={handleClearDtrLogs} 
                      variant="destructive"
                      size="sm"
                      className="h-9 sm:h-10"
                      data-testid="btn-clear-dtr"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear {selectedUserId ? 'User' : 'All'} Logs
                    </Button>
                  </div>
                </div>
                {/* Summary */}
                <div className="mt-4 p-3 bg-muted/30 rounded-md">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      Total: <span className="font-semibold text-foreground">{filteredDtrEntries.length}</span> entries | 
                      Total Hours: <span className="font-semibold text-foreground">{filteredDtrEntries.reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0).toFixed(1)}h</span>
                    </span>
                    {filteredDtrEntries.length > 10 && (
                      <span className="text-xs text-muted-foreground">Showing latest 10 of {filteredDtrEntries.length}</span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredDtrEntries.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border/40">
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Date</th>
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Employee</th>
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Department</th>
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Project</th>
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Time In</th>
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Time Out</th>
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Hours</th>
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Work Description</th>
                          <th className="text-left py-3 px-2 font-medium text-muted-foreground">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDtrEntries.slice(0, 10).map((entry) => (
                          <tr 
                            key={entry.id} 
                            className="border-b border-border/20 hover:bg-muted/30"
                            data-testid={`dtr-row-${entry.id}`}
                          >
                            <td className="py-3 px-2 text-foreground">{entry.date}</td>
                            <td className="py-3 px-2 text-foreground font-medium">{entry.user_name}</td>
                            <td className="py-3 px-2">
                              <span className={`px-2 py-1 text-xs rounded capitalize ${
                                entry.department?.toLowerCase() === 'design' 
                                  ? 'bg-primary/20 text-primary' 
                                  : 'bg-accent/20 text-accent'
                              }`}>
                                {entry.department || 'N/A'}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-foreground">{entry.project}</td>
                            <td className="py-3 px-2 text-foreground font-mono">{entry.time_in || '-'}</td>
                            <td className="py-3 px-2 text-foreground font-mono">{entry.time_out || '-'}</td>
                            <td className="py-3 px-2 text-foreground font-mono">{entry.hours}h</td>
                            <td className="py-3 px-2 text-muted-foreground max-w-xs truncate" title={entry.work_description}>
                              {entry.work_description || '-'}
                            </td>
                            <td className="py-3 px-2">
                              <button
                                onClick={() => handleDeleteDtr(entry.id)}
                                className="p-2 hover:bg-muted rounded"
                                data-testid={`btn-delete-dtr-${entry.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-8">No DTR submissions yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ot" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="ot-applications-card">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <Timer className="w-5 h-5 text-purple-500" />
                    <CardTitle>OT Applications</CardTitle>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-muted-foreground" />
                      <select
                        data-testid="select-ot-employee-filter"
                        className="flex h-10 w-[200px] rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                        value={selectedOtUserId}
                        onChange={(e) => setSelectedOtUserId(e.target.value)}
                      >
                        <option value="">All Employees</option>
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.full_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <Button 
                      onClick={handleExportOtSummary} 
                      variant="outline"
                      data-testid="btn-export-ot"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export PDF
                    </Button>
                  </div>
                </div>
                {/* Summary */}
                <div className="mt-4 p-3 bg-muted/30 rounded-md">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      Total: <span className="font-semibold text-foreground">{filteredOtApplications.length}</span> | 
                      Pending: <span className="font-semibold text-yellow-500">{filteredOtApplications.filter(o => o.status === 'pending').length}</span> | 
                      Approved: <span className="font-semibold text-green-500">{filteredOtApplications.filter(o => o.status === 'approved').length}</span> | 
                      Total Hours: <span className="font-semibold text-purple-500">{filteredOtApplications.reduce((sum, o) => sum + (parseFloat(o.hours) || 0), 0).toFixed(1)}h</span>
                    </span>
                    {filteredOtApplications.length > 10 && (
                      <button onClick={() => setShowAllOt(v => !v)} className="text-xs text-primary hover:underline font-medium" data-testid="btn-toggle-all-ot">
                        {showAllOt ? 'Show latest 10' : `Show all ${filteredOtApplications.length} previous`}
                      </button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredOtApplications.length > 0 ? (
                  <div className="space-y-4">
                    {(showAllOt ? filteredOtApplications : filteredOtApplications.slice(0, 10)).map((ot) => (
                      <div 
                        key={ot.id} 
                        className={`p-4 rounded-md border ${
                          ot.status === 'approved' ? 'bg-green-500/10 border-green-500/30' :
                          ot.status === 'declined' ? 'bg-red-500/10 border-red-500/30' :
                          'bg-muted/30 border-border/40'
                        }`}
                        data-testid={`ot-row-${ot.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-foreground">{ot.date}</h4>
                              <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                                ot.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                                ot.status === 'declined' ? 'bg-red-500/20 text-red-500' :
                                'bg-yellow-500/20 text-yellow-500'
                              }`}>
                                {ot.status}
                              </span>
                              <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-500 rounded font-mono">
                                {ot.hours} hour(s)
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">{ot.justification}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              By: {ot.user_name || 'Unknown'} | Dept: {ot.department || 'N/A'} | {new Date(ot.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 pt-2 border-t border-border/40">
                          {ot.status === 'pending' && (
                            <>
                              <Button 
                                size="sm" 
                                onClick={() => handleApproveOt(ot.id)}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid={`btn-approve-ot-${ot.id}`}
                              >
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleDeclineOt(ot.id)}
                                data-testid={`btn-decline-ot-${ot.id}`}
                              >
                                Decline
                              </Button>
                            </>
                          )}
                          {ot.status !== 'pending' && ot.approved_by_name && (
                            <span className="text-xs text-muted-foreground">
                              {ot.status === 'approved' ? 'Approved' : 'Declined'} by {ot.approved_by_name}
                            </span>
                          )}
                          {ot.status === 'approved' && ot.ot_code && (
                            <span className="ml-2 text-xs font-mono bg-green-500/20 text-green-500 px-2 py-1 rounded">
                              {ot.ot_code}
                            </span>
                          )}
                          <button
                            onClick={() => handleDeleteOt(ot.id)}
                            className="p-2 hover:bg-muted rounded ml-auto"
                            data-testid={`btn-delete-ot-${ot.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-8">No OT applications yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="leave" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="leave-applications-card">
              <CardHeader className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <CalendarDays className="w-5 h-5 text-blue-500" />
                    <CardTitle className="text-base sm:text-xl">Leave Applications</CardTitle>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <select
                      data-testid="select-leave-employee-filter"
                      className="flex h-9 w-full sm:w-[180px] rounded-md border border-input bg-background/50 px-2 py-1 text-sm"
                      value={selectedLeaveUserId}
                      onChange={(e) => setSelectedLeaveUserId(e.target.value)}
                    >
                      <option value="">All Employees</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.full_name}
                        </option>
                      ))}
                    </select>
                    <Button 
                      onClick={handleExportLeaveSummary} 
                      variant="outline"
                      size="sm"
                      className="h-9"
                      data-testid="btn-export-leave"
                    >
                      <Download className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Export PDF</span>
                    </Button>
                  </div>
                </div>
                {/* Summary - Mobile optimized */}
                <div className="mt-4 p-3 bg-muted/30 rounded-md">
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs sm:text-sm text-muted-foreground">
                    <span>Total: <span className="font-semibold text-foreground">{filteredLeaveApplications.length}</span></span>
                    <span>Pending: <span className="font-semibold text-yellow-500">{filteredLeaveApplications.filter(l => l.status === 'pending').length}</span></span>
                    <span>Approved: <span className="font-semibold text-green-500">{filteredLeaveApplications.filter(l => l.status === 'approved').length}</span></span>
                    <span>Days: <span className="font-semibold text-blue-500">{filteredLeaveApplications.reduce((sum, l) => sum + (parseInt(l.days_requested) || 0), 0)}</span></span>
                  </div>
                  {filteredLeaveApplications.length > 10 && (
                    <p className="text-xs text-muted-foreground mt-2">Showing latest 10 of {filteredLeaveApplications.length}</p>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {filteredLeaveApplications.length > 0 ? (
                  <div className="space-y-4">
                    {filteredLeaveApplications.slice(0, 10).map((leave) => (
                      <div 
                        key={leave.id} 
                        className={`p-4 rounded-md border ${
                          leave.status === 'approved' ? 'bg-green-500/10 border-green-500/30' :
                          leave.status === 'declined' ? 'bg-red-500/10 border-red-500/30' :
                          'bg-muted/30 border-border/40'
                        }`}
                        data-testid={`leave-row-${leave.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-foreground">{getLeaveTypeName(leave.leave_type)}</h4>
                              <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                                leave.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                                leave.status === 'declined' ? 'bg-red-500/20 text-red-500' :
                                'bg-yellow-500/20 text-yellow-500'
                              }`}>
                                {leave.status}
                              </span>
                              <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-500 rounded">
                                {leave.days_requested} day(s)
                              </span>
                            </div>
                            <p className="text-sm text-foreground">
                              {leave.start_date} to {leave.end_date}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">{leave.reason}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              By: {leave.user_name || 'Unknown'} | Dept: {leave.department || 'N/A'} | {new Date(leave.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 pt-2 border-t border-border/40">
                          {leave.status === 'pending' && (
                            <>
                              <Button 
                                size="sm" 
                                onClick={() => handleApproveLeave(leave.id)}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid={`btn-approve-leave-${leave.id}`}
                              >
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleDeclineLeave(leave.id)}
                                data-testid={`btn-decline-leave-${leave.id}`}
                              >
                                Decline
                              </Button>
                            </>
                          )}
                          {leave.status !== 'pending' && leave.approved_by_name && (
                            <span className="text-xs text-muted-foreground">
                              {leave.status === 'approved' ? 'Approved' : 'Declined'} by {leave.approved_by_name}
                            </span>
                          )}
                          <button
                            onClick={() => handleDeleteLeave(leave.id)}
                            className="p-2 hover:bg-muted rounded ml-auto"
                            data-testid={`btn-delete-leave-${leave.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-8">No leave applications yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reimbursement" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="reimbursement-submissions-card">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-green-500" />
                    <CardTitle>Reimbursement Submissions</CardTitle>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {reimbursements.length} submission(s) | Auto-deleted after 2 months
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                {reimbursements.length > 0 ? (
                  <>
                  <div className="space-y-4">
                    {reimbPage.items.map((reimb) => (
                      <div 
                        key={reimb.id} 
                        className={`p-4 rounded-md border ${
                          reimb.status === 'paid' ? 'bg-emerald-500/10 border-emerald-500/40' :
                          reimb.status === 'for_payment' ? 'bg-blue-500/10 border-blue-500/40' :
                          reimb.status === 'approved' ? 'bg-green-500/10 border-green-500/30' :
                          reimb.status === 'rejected' || reimb.status === 'declined' ? 'bg-red-500/10 border-red-500/30' :
                          'bg-muted/30 border-border/40'
                        }`}
                        data-testid={`reimb-row-${reimb.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-foreground">₱{reimb.amount?.toLocaleString()}</h4>
                              <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                                reimb.status === 'paid' ? 'bg-emerald-500/25 text-emerald-600 font-bold' :
                                reimb.status === 'for_payment' ? 'bg-blue-500/25 text-blue-600 font-bold' :
                                reimb.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                                reimb.status === 'rejected' || reimb.status === 'declined' ? 'bg-red-500/20 text-red-500' :
                                'bg-accent/20 text-accent'
                              }`}>
                                {reimb.status === 'for_payment' ? 'For Payment' : reimb.status}
                              </span>
                            </div>
                            <p className="text-sm text-foreground">{reimb.description}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              By: {reimb.user_name || 'Unknown'} | Dept: {reimb.department || 'N/A'} | Project: {reimb.project_name || 'N/A'} | {new Date(reimb.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {reimb.receipt_url && (
                              <a 
                                href={`${API_URL}${reimb.receipt_url}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-primary hover:underline text-xs flex items-center gap-1"
                              >
                                <Paperclip className="w-3 h-3" />
                                Receipt
                              </a>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 pt-2 border-t border-border/40 flex-wrap">
                          {reimb.status === 'pending' && (
                            <>
                              <Button 
                                size="sm" 
                                onClick={() => handleApproveReimbursement(reimb.id)}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid={`btn-approve-${reimb.id}`}
                              >
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleDeclineReimbursement(reimb.id)}
                                data-testid={`btn-decline-${reimb.id}`}
                              >
                                Decline
                              </Button>
                            </>
                          )}
                          {reimb.status === 'approved' && (
                            <Button
                              size="sm"
                              onClick={() => handleSetReimbStatus(reimb.id, 'for_payment', 'For Payment')}
                              className="bg-blue-600 hover:bg-blue-700"
                              data-testid={`btn-for-payment-${reimb.id}`}
                            >
                              For Payment
                            </Button>
                          )}
                          {reimb.status === 'for_payment' && (
                            <Button
                              size="sm"
                              onClick={() => handleSetReimbStatus(reimb.id, 'paid', 'Paid')}
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`btn-mark-paid-${reimb.id}`}
                            >
                              Mark as Paid
                            </Button>
                          )}
                          {reimb.status !== 'pending' && reimb.approved_by_name && (
                            <span className="text-xs text-muted-foreground">
                              {reimb.status === 'paid' && reimb.paid_by_name ? `Paid by ${reimb.paid_by_name}` :
                                reimb.status === 'for_payment' ? `Approved by ${reimb.approved_by_name}` :
                                reimb.status === 'approved' ? `Approved by ${reimb.approved_by_name}` :
                                `${reimb.status === 'rejected' ? 'Declined' : reimb.status} by ${reimb.approved_by_name}`}
                            </span>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleExportReimbursement(reimb.id)}
                            data-testid={`btn-export-reimb-${reimb.id}`}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Export
                          </Button>
                          <button
                            onClick={() => handleDeleteReimbursement(reimb.id)}
                            className="p-2 hover:bg-muted rounded"
                            data-testid={`btn-delete-reimb-${reimb.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Pagination page={reimbPage.page} totalPages={reimbPage.totalPages} setPage={reimbPage.setPage} total={reimbPage.total} pageSize={reimbPage.pageSize} label="submissions" testIdPrefix="reimb-pg" />
                  </>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-8">No reimbursement submissions yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payvault" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="pay-vault-card">
              <CardHeader className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Wallet className="w-5 h-5 text-emerald-500" />
                    <CardTitle className="text-base sm:text-xl">Pay Vault</CardTitle>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <select
                      data-testid="select-payvault-employee-filter"
                      className="flex h-9 w-full sm:w-[180px] rounded-md border border-input bg-background/50 px-2 py-1 text-sm"
                      value={selectedPayVaultUserId}
                      onChange={(e) => setSelectedPayVaultUserId(e.target.value)}
                    >
                      <option value="">All Employees</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.full_name}
                        </option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => setShowPayslipDialog(true)}
                        data-testid="btn-upload-payslip"
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 h-9 flex-1 sm:flex-none"
                      >
                        <Upload className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Payslip</span>
                      </Button>
                      <Button 
                        onClick={() => setShowItrDialog(true)}
                        variant="outline"
                        size="sm"
                        className="h-9 flex-1 sm:flex-none"
                        data-testid="btn-upload-itr"
                      >
                        <Upload className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">ITR</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Payslips Section */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-emerald-500" />
                      Payslips ({filteredPayslips.length})
                    </h3>
                    {sortedYears.length > 0 ? (
                      <div className="space-y-4 max-h-[500px] overflow-y-auto">
                        {sortedYears.map((year) => (
                          <div key={year} className="space-y-2">
                            <h4 className="text-sm font-medium text-muted-foreground border-b border-border/40 pb-1">{year}</h4>
                            {groupedPayslips[year].map((payslip) => (
                              <div 
                                key={payslip.id} 
                                className="flex items-center justify-between p-3 bg-muted/30 rounded-md"
                                data-testid={`payslip-${payslip.id}`}
                              >
                                <div>
                                  <p className="font-medium text-foreground">{payslip.month_name} {payslip.year}</p>
                                  <p className="text-xs text-muted-foreground">{payslip.user_name}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <a 
                                    href={`${API_URL}${payslip.file_url}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs text-emerald-500 hover:underline"
                                  >
                                    View PDF
                                  </a>
                                  <button
                                    onClick={() => handleDeletePayslip(payslip.id)}
                                    className="p-1 hover:bg-muted rounded"
                                    data-testid={`btn-delete-payslip-${payslip.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm text-center py-8">No payslips uploaded yet</p>
                    )}
                  </div>

                  {/* ITR Documents Section */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-500" />
                      ITR Documents / BIR Form 2316 ({filteredItrDocuments.length})
                    </h3>
                    {filteredItrDocuments.length > 0 ? (
                      <div className="space-y-2 max-h-[500px] overflow-y-auto">
                        {filteredItrDocuments.map((itr) => (
                          <div 
                            key={itr.id} 
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-md"
                            data-testid={`itr-${itr.id}`}
                          >
                            <div>
                              <p className="font-medium text-foreground">ITR {itr.year}</p>
                              <p className="text-xs text-muted-foreground">{itr.user_name}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <a 
                                href={`${API_URL}${itr.file_url}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-blue-500 hover:underline"
                              >
                                View PDF
                              </a>
                              <button
                                onClick={() => handleDeleteItr(itr.id)}
                                className="p-1 hover:bg-muted rounded"
                                data-testid={`btn-delete-itr-${itr.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm text-center py-8">No ITR documents uploaded yet</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="employees" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="employees-management-card">
              <CardHeader className="p-4 sm:p-6">
                <div className="flex items-center gap-3">
                  <UserCog className="w-5 h-5 text-violet-500" />
                  <CardTitle className="text-base sm:text-xl">Employees</CardTitle>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground mt-2">
                  Manage employee accounts, roles, and leave credits
                </p>
                <div className="mt-4 p-2 sm:p-3 bg-muted/20 rounded-md border border-border/40">
                  <p className="text-xs font-medium text-foreground mb-2">Access Levels:</p>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-2 text-xs">
                    <span className="px-2 py-1 rounded bg-violet-500/20 text-violet-500">Admin/Principal/HR = Full</span>
                    <span className="px-2 py-1 rounded bg-amber-500/20 text-amber-500">PM/Design Lead = Projects</span>
                    <span className="px-2 py-1 rounded bg-muted text-muted-foreground">Design/Construction = Basic</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {users.length > 0 ? (
                  <>
                  <div className="space-y-3">
                    {employeePage.items.map((emp) => (
                      <div 
                        key={emp.id} 
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 bg-muted/30 rounded-md border border-border/40 gap-3"
                        data-testid={`employee-row-${emp.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-foreground text-sm sm:text-base truncate">{emp.full_name}</h4>
                          <p className="text-xs sm:text-sm text-muted-foreground truncate">{emp.email}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {emp.phone_number || emp.phone || 'No phone'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                          {/* Department Dropdown */}
                          <select
                            value={emp.department || ''}
                            onChange={(e) => handleDepartmentChange(emp.id, e.target.value, emp.full_name)}
                            className="text-xs px-2 py-1.5 rounded border border-border bg-background/50 w-full sm:w-auto cursor-pointer"
                            data-testid={`select-department-${emp.id}`}
                          >
                            <option value="">No Dept</option>
                            {DEPARTMENT_OPTIONS.map((dept) => (
                              <option key={dept.value} value={dept.value}>
                                {dept.label}
                              </option>
                            ))}
                          </select>
                          {/* Role Dropdown */}
                          <select
                            value={emp.role}
                            onChange={(e) => handleRoleChange(emp.id, e.target.value, emp.full_name)}
                            disabled={emp.id === user.id} 
                            className={`text-xs px-2 py-1.5 rounded border border-border bg-background/50 w-full sm:w-auto ${
                              emp.id === user.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                            }`}
                            data-testid={`select-role-${emp.id}`}
                          >
                            {ROLE_OPTIONS.map((role) => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openLeaveCreditsDialog(emp)}
                            className="h-8 text-xs"
                            data-testid={`btn-adjust-credits-${emp.id}`}
                          >
                            <CalendarDays className="w-3 h-3 sm:mr-1" />
                            <span className="hidden sm:inline">Credits</span>
                          </Button>
                          {emp.id !== user.id && (
                            <button
                              onClick={() => handleDeleteUser(emp.id, emp.full_name)}
                              className="p-2 hover:bg-muted rounded"
                              data-testid={`btn-delete-user-${emp.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Pagination page={employeePage.page} totalPages={employeePage.totalPages} setPage={employeePage.setPage} total={employeePage.total} pageSize={employeePage.pageSize} label="employees" testIdPrefix="emp-pg" />
                  </>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-8">No employees found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="policies" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="policies-management-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Link className="w-5 h-5 text-indigo-500" />
                  <CardTitle>Department Policy Links</CardTitle>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Configure Google Drive links for each department's policies, memos, guidelines, and protocols.
                </p>
              </CardHeader>
              <CardContent>
                {departmentPolicyLinks.length > 0 ? (
                  <div className="space-y-6">
                    {departmentPolicyLinks.map((deptData) => (
                      <div key={deptData.department.id} className="border border-border/40 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-4">
                          {deptData.department.id === 'design' && <Pencil className="w-5 h-5 text-primary" />}
                          {deptData.department.id === 'construction' && <HardHat className="w-5 h-5 text-orange-500" />}
                          {deptData.department.id === 'administrators' && <Shield className="w-5 h-5 text-purple-500" />}
                          {deptData.department.id === 'hr' && <Users className="w-5 h-5 text-pink-500" />}
                          {deptData.department.id === 'supply_chain' && <Truck className="w-5 h-5 text-cyan-500" />}
                          {deptData.department.id === 'accounting' && <Calculator className="w-5 h-5 text-emerald-500" />}
                          <h3 className="font-semibold text-foreground">{deptData.department.name}</h3>
                        </div>
                        <div className="grid gap-3">
                          {deptData.policies.map((policy) => (
                            <PolicyLinkEditor
                              key={policy.policy_type.id}
                              departmentId={deptData.department.id}
                              policyType={policy.policy_type}
                              currentLink={policy.google_drive_link || ''}
                              updatedAt={policy.updated_at}
                              updatedByName={policy.updated_by_name}
                              onSave={handleUpdatePolicyLink}
                              onDelete={handleDeletePolicyLink}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Link className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading department policy links...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Logs Tab - Principal Only */}
          {user.role === 'principal' && (
          <TabsContent value="audit" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="audit-logs-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <ClipboardList className="w-5 h-5 text-amber-500" />
                  <CardTitle>Audit Logs</CardTitle>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Track role changes and important administrative actions (Principal access only)
                </p>
              </CardHeader>
              <CardContent>
                {auditLogs.length > 0 ? (
                  <>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {auditPage.items.map((log) => (
                      <div
                        key={log.id}
                        className="p-4 bg-muted/30 rounded-md border border-border/40"
                        data-testid={`audit-log-${log.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                                log.action === 'role_change' ? 'bg-amber-500/20 text-amber-500' :
                                log.action === 'user_delete' ? 'bg-red-500/20 text-red-500' :
                                'bg-muted text-muted-foreground'
                              }`}>
                                {log.action.replace('_', ' ')}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(log.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-foreground">
                              <span className="font-medium">{log.actor_name}</span>
                              {' → '}
                              <span className="font-medium">{log.target_name}</span>
                            </p>
                            {log.details && (
                              <p className="text-sm text-muted-foreground mt-1">{log.details}</p>
                            )}
                            {log.old_value && log.new_value && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Changed from <span className="text-amber-500">{log.old_value}</span> to <span className="text-emerald-500">{log.new_value}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Pagination page={auditPage.page} totalPages={auditPage.totalPages} setPage={auditPage.setPage} total={auditPage.total} pageSize={auditPage.pageSize} label="logs" testIdPrefix="audit-pg" />
                  </>
                ) : (
                  <div className="text-center py-12">
                    <ClipboardList className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No audit logs recorded yet</p>
                    <p className="text-sm text-muted-foreground mt-1">Role changes and administrative actions will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}

            </Tabs>
          </TabsContent>
          )}
        </Tabs>

        {/* DPR View Dialog */}
        <Dialog open={showDprDialog} onOpenChange={setShowDprDialog}>
          <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Daily Progress Report Details</DialogTitle>
            </DialogHeader>
            {selectedDpr && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Project</p>
                    <p className="font-semibold text-foreground">{selectedDpr.project}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Date</p>
                    <p className="font-semibold text-foreground">{new Date(selectedDpr.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground uppercase">Submitted By</p>
                    <p className="text-foreground">{selectedDpr.submitted_by_name}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-2">Accomplished Works</p>
                  <div className="p-3 bg-muted/30 rounded-md">
                    <p className="text-foreground whitespace-pre-wrap">{selectedDpr.accomplished_works}</p>
                  </div>
                </div>
                
                {selectedDpr.site_photos && selectedDpr.site_photos.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase mb-2">Site Photos ({selectedDpr.site_photos.length})</p>
                    <div className="grid grid-cols-3 gap-2">
                      {selectedDpr.site_photos.map((photo, idx) => (
                        <a 
                          key={idx} 
                          href={`${API_URL}${photo}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <img 
                            src={`${API_URL}${photo}`} 
                            alt={`Site photo ${idx + 1}`}
                            className="w-full h-32 object-cover rounded-md border border-border hover:opacity-80 transition-opacity"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedDpr.challenges && (
                  <div>
                    <p className="text-xs text-orange-500 uppercase mb-2">Challenges / Urgent Items</p>
                    <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-md">
                      <p className="text-orange-400 whitespace-pre-wrap">{selectedDpr.challenges}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowDprDialog(false)}>Close</Button>
                  <Button onClick={() => handleExportDpr(selectedDpr.id)}>
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>
                {editingProject ? 'Edit Project' : 'Add Project'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitProject} className="space-y-4">
              <div>
                <Label htmlFor="project_name_dir">Project Name</Label>
                <Input
                  id="project_name_dir"
                  data-testid="input-project-name-dir"
                  value={projectForm.project_name}
                  onChange={(e) => setProjectForm({ ...projectForm, project_name: e.target.value })}
                  placeholder="Modern Villa Renovation"
                  required
                />
              </div>
              <div>
                <Label htmlFor="project_code">Project Code</Label>
                <Input
                  id="project_code"
                  data-testid="input-project-code"
                  value={projectForm.project_code}
                  onChange={(e) => setProjectForm({ ...projectForm, project_code: e.target.value.toUpperCase() })}
                  placeholder="MVR-2026"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">Unique identifier for this project</p>
              </div>
              <div>
                <Label htmlFor="project_client_name">Client's Name</Label>
                <Input
                  id="project_client_name"
                  data-testid="input-project-client-name"
                  value={projectForm.client_name}
                  onChange={(e) => setProjectForm({ ...projectForm, client_name: e.target.value })}
                  placeholder="e.g. John Felipe Nabong"
                />
              </div>
              <div>
                <Label htmlFor="project_address">Project Address</Label>
                <Textarea
                  id="project_address"
                  data-testid="input-project-address"
                  value={projectForm.project_address}
                  onChange={(e) => setProjectForm({ ...projectForm, project_address: e.target.value })}
                  placeholder="Street, Barangay, City, Province"
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="project_description">Description</Label>
                <Textarea
                  id="project_description"
                  data-testid="input-project-description"
                  value={projectForm.description}
                  onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                  placeholder="Comprehensive renovation of a modern villa including structural upgrades..."
                  rows={4}
                  required
                />
              </div>
              <Button type="submit" className="w-full" data-testid="btn-submit-project">
                {editingProject ? 'Update Project' : 'Add Project'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>
                {editingSchedule ? 'Edit Project Schedule' : 'Add Project Schedule'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="project_name">Project Name</Label>
                <Input
                  id="project_name"
                  data-testid="input-project-name"
                  value={scheduleForm.project_name}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, project_name: e.target.value })}
                  placeholder="Modern Villa Project"
                  required
                />
              </div>
              <div>
                <Label htmlFor="instagantt_link">Instagantt Link</Label>
                <Input
                  id="instagantt_link"
                  type="url"
                  data-testid="input-instagantt-link"
                  value={scheduleForm.instagantt_link}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, instagantt_link: e.target.value })}
                  placeholder="https://instagantt.com/..."
                  required
                />
              </div>
              {!editingSchedule && (
                <div>
                  <Label htmlFor="department">Department</Label>
                  <select
                    id="department"
                    data-testid="select-department"
                    className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                    value={scheduleForm.department}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, department: e.target.value })}
                  >
                    <option value="design">Design</option>
                    <option value="construction">Construction</option>
                  </select>
                </div>
              )}
              <Button type="submit" className="w-full" data-testid="btn-submit-schedule">
                {editingSchedule ? 'Update Schedule' : 'Add Schedule'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Payslip Upload Dialog */}
        <Dialog open={showPayslipDialog} onOpenChange={setShowPayslipDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Upload Payslip</DialogTitle>
            </DialogHeader>
            <form onSubmit={handlePayslipUpload} className="space-y-4">
              <div>
                <Label htmlFor="payslip_employee">Employee</Label>
                <select
                  id="payslip_employee"
                  data-testid="select-payslip-employee"
                  className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                  value={payslipForm.user_id}
                  onChange={(e) => setPayslipForm({ ...payslipForm, user_id: e.target.value })}
                  required
                >
                  <option value="">Select Employee</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="payslip_month">Month</Label>
                  <select
                    id="payslip_month"
                    data-testid="select-payslip-month"
                    className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                    value={payslipForm.month}
                    onChange={(e) => setPayslipForm({ ...payslipForm, month: parseInt(e.target.value) })}
                    required
                  >
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="payslip_year">Year</Label>
                  <Input
                    id="payslip_year"
                    type="number"
                    data-testid="input-payslip-year"
                    value={payslipForm.year}
                    onChange={(e) => setPayslipForm({ ...payslipForm, year: parseInt(e.target.value) })}
                    min="2020"
                    max="2030"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="payslip_file">PDF File</Label>
                <Input
                  id="payslip_file"
                  type="file"
                  accept=".pdf"
                  data-testid="input-payslip-file"
                  onChange={(e) => setPayslipForm({ ...payslipForm, file: e.target.files[0] })}
                  required
                  className="cursor-pointer"
                />
                <p className="text-xs text-muted-foreground mt-1">Only PDF files are allowed</p>
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="btn-submit-payslip" disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload Payslip'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* ITR Upload Dialog */}
        <Dialog open={showItrDialog} onOpenChange={setShowItrDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Upload ITR Document (BIR Form 2316)</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleItrUpload} className="space-y-4">
              <div>
                <Label htmlFor="itr_employee">Employee</Label>
                <select
                  id="itr_employee"
                  data-testid="select-itr-employee"
                  className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                  value={itrForm.user_id}
                  onChange={(e) => setItrForm({ ...itrForm, user_id: e.target.value })}
                  required
                >
                  <option value="">Select Employee</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="itr_year">Year</Label>
                <Input
                  id="itr_year"
                  type="number"
                  data-testid="input-itr-year"
                  value={itrForm.year}
                  onChange={(e) => setItrForm({ ...itrForm, year: parseInt(e.target.value) })}
                  min="2020"
                  max="2030"
                  required
                />
              </div>
              <div>
                <Label htmlFor="itr_file">PDF File</Label>
                <Input
                  id="itr_file"
                  type="file"
                  accept=".pdf"
                  data-testid="input-itr-file"
                  onChange={(e) => setItrForm({ ...itrForm, file: e.target.files[0] })}
                  required
                  className="cursor-pointer"
                />
                <p className="text-xs text-muted-foreground mt-1">Only PDF files are allowed</p>
              </div>
              <Button type="submit" className="w-full" data-testid="btn-submit-itr" disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload ITR Document'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Policy Upload Dialog */}
        <Dialog open={showPolicyDialog} onOpenChange={setShowPolicyDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>{editingPolicy ? 'Update Policy Document' : 'Upload Policy Document'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handlePolicyUpload} className="space-y-4">
              <div>
                <Label htmlFor="policy_type">Policy Type</Label>
                <select
                  id="policy_type"
                  data-testid="select-policy-type"
                  className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                  value={policyForm.policy_type}
                  onChange={(e) => setPolicyForm({ ...policyForm, policy_type: e.target.value })}
                  disabled={editingPolicy}
                  required
                >
                  {POLICY_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="policy_title">Title</Label>
                <Input
                  id="policy_title"
                  data-testid="input-policy-title"
                  value={policyForm.title}
                  onChange={(e) => setPolicyForm({ ...policyForm, title: e.target.value })}
                  placeholder="Employee Handbook 2025"
                  required
                />
              </div>
              <div>
                <Label htmlFor="policy_description">Description</Label>
                <Textarea
                  id="policy_description"
                  data-testid="input-policy-description"
                  value={policyForm.description}
                  onChange={(e) => setPolicyForm({ ...policyForm, description: e.target.value })}
                  placeholder="Code of conduct, dress code guidelines, and workplace standards."
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="policy_version">Version</Label>
                <Input
                  id="policy_version"
                  data-testid="input-policy-version"
                  value={policyForm.version}
                  onChange={(e) => setPolicyForm({ ...policyForm, version: e.target.value })}
                  placeholder="1.0"
                />
              </div>
              <div>
                <Label htmlFor="policy_file">PDF File {editingPolicy && '(Leave empty to keep current file)'}</Label>
                <Input
                  id="policy_file"
                  type="file"
                  accept=".pdf"
                  data-testid="input-policy-file"
                  onChange={(e) => setPolicyForm({ ...policyForm, file: e.target.files[0] })}
                  required={!editingPolicy}
                  className="cursor-pointer"
                />
                <p className="text-xs text-muted-foreground mt-1">Only PDF files are allowed</p>
              </div>
              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="btn-submit-policy" disabled={uploading}>
                {uploading ? 'Uploading...' : (editingPolicy ? 'Update Policy' : 'Upload Policy')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Leave Credits Adjustment Dialog */}
        <Dialog open={showLeaveCreditsDialog} onOpenChange={setShowLeaveCreditsDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Adjust Leave Credits - {selectedEmployeeForCredits?.full_name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateLeaveCredits} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Vacation Leave (Total)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    data-testid="input-vacation-leave"
                    value={leaveCreditsForm.vacation_leave}
                    onChange={(e) => setLeaveCreditsForm({ ...leaveCreditsForm, vacation_leave: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Vacation Used</Label>
                  <Input
                    type="number"
                    step="0.5"
                    data-testid="input-vacation-used"
                    value={leaveCreditsForm.vacation_used}
                    onChange={(e) => setLeaveCreditsForm({ ...leaveCreditsForm, vacation_used: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Sick Leave (Total)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    data-testid="input-sick-leave"
                    value={leaveCreditsForm.sick_leave}
                    onChange={(e) => setLeaveCreditsForm({ ...leaveCreditsForm, sick_leave: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Sick Used</Label>
                  <Input
                    type="number"
                    step="0.5"
                    data-testid="input-sick-used"
                    value={leaveCreditsForm.sick_used}
                    onChange={(e) => setLeaveCreditsForm({ ...leaveCreditsForm, sick_used: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Emergency Leave (Total)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    data-testid="input-emergency-leave"
                    value={leaveCreditsForm.emergency_leave}
                    onChange={(e) => setLeaveCreditsForm({ ...leaveCreditsForm, emergency_leave: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Emergency Used</Label>
                  <Input
                    type="number"
                    step="0.5"
                    data-testid="input-emergency-used"
                    value={leaveCreditsForm.emergency_used}
                    onChange={(e) => setLeaveCreditsForm({ ...leaveCreditsForm, emergency_used: parseFloat(e.target.value) })}
                  />
                </div>
              </div>
              <div className="p-3 bg-muted/30 rounded-md">
                <p className="text-sm text-muted-foreground">
                  <strong>Remaining Credits:</strong><br />
                  VL: {(leaveCreditsForm.vacation_leave - leaveCreditsForm.vacation_used).toFixed(1)} | 
                  SL: {(leaveCreditsForm.sick_leave - leaveCreditsForm.sick_used).toFixed(1)} | 
                  Emergency: {(leaveCreditsForm.emergency_leave - leaveCreditsForm.emergency_used).toFixed(1)}
                </p>
              </div>
              <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700" data-testid="btn-save-credits">
                Save Leave Credits
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

// PolicyLinkEditor Component for editing individual policy links
function PolicyLinkEditor({ departmentId, policyType, currentLink, updatedAt, updatedByName, onSave, onDelete }) {
  const [link, setLink] = useState(currentLink);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!link.trim()) {
      toast.error('Please enter a valid Google Drive link');
      return;
    }
    setSaving(true);
    try {
      await onSave(departmentId, policyType.id, link);
      setIsEditing(false);
    } catch (error) {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentLink) return;
    await onDelete(departmentId, policyType.id);
    setLink('');
    setIsEditing(false);
  };

  const policyIcons = {
    memos: <FileText className="w-4 h-4 text-blue-400" />,
    policies: <BookOpen className="w-4 h-4 text-indigo-400" />,
    guidelines: <ListChecks className="w-4 h-4 text-teal-400" />,
    protocols: <ClipboardList className="w-4 h-4 text-amber-400" />,
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-muted/20 rounded-md">
      <div className="flex items-center gap-2 min-w-[120px]">
        {policyIcons[policyType.id] || <FileText className="w-4 h-4" />}
        <span className="text-sm font-medium text-foreground">{policyType.name}</span>
      </div>
      
      <div className="flex-1">
        {isEditing ? (
          <Input
            type="url"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://drive.google.com/..."
            className="text-sm"
            data-testid={`input-link-${departmentId}-${policyType.id}`}
          />
        ) : (
          <div className="text-sm">
            {currentLink ? (
              <a 
                href={currentLink} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                {currentLink.length > 50 ? currentLink.substring(0, 50) + '...' : currentLink}
              </a>
            ) : (
              <span className="text-muted-foreground italic">No link configured</span>
            )}
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-2">
        {isEditing ? (
          <>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="h-8 px-3 bg-green-600 hover:bg-green-700"
              data-testid={`btn-save-link-${departmentId}-${policyType.id}`}
            >
              {saving ? '...' : <Save className="w-4 h-4" />}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setLink(currentLink);
                setIsEditing(false);
              }}
              className="h-8 px-3"
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsEditing(true)}
              className="h-8 px-3"
              data-testid={`btn-edit-link-${departmentId}-${policyType.id}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            {currentLink && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleDelete}
                className="h-8 px-3 text-destructive hover:bg-destructive/10"
                data-testid={`btn-delete-link-${departmentId}-${policyType.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
